import { EMPTY, merge, Observable } from 'rxjs'
import { filter, switchMap, map } from 'rxjs/operators'
import Opportunity from './Opportunity'
import { OpportunityEvent } from './opportunity-events'

/**
 *  The action to be taken upon emission.
 */
export enum SwoopingAction {
  /**
   *  Deselect the opportunity and show the vanished dialog.
   */
  DeselectAndShowVanished,

  /**
   * Only deselect and don't show the vanished dialog.
   */
  OnlyDeselect,
}

/**
 * This emits a value when the opportunity being viewed
 * vanishes (someone picked it up), and if the value emitted
 * is `true`, shows the Vanished dialog.
 *
 * @param vanishedOnPickup$ a stream of explicit "not fast enough" triggers
 *  from the user trying to pick up opportunities.
 *
 * @param selected$ a stream of op selection changes, used
 *  for tracking the currently selected opportunity.
 *
 * @param events$ a stream of events for opportunities.
 *
 * @returns a stream. When it emits we want to show
 *  the vanished dialog.
 */
export function OpportunitySwooping(
  vanishedOnPickup$: Observable<unknown>,
  selected$: Observable<Opportunity | null>,
  events$: Observable<OpportunityEvent>,
): Observable<SwoopingAction> {
  // Gets a stream that emits when the opportunity gets removed
  // where the opportunity was not picked up by the current user.
  const opportunityGetsRemoved = (op: Opportunity) =>
    events$.pipe(
      filter(
        (e) =>
          e.type === 'OpportunityRemoved' &&
          e.opportunity === op &&
          // This check is what guarantees that we won't see the Vanished
          // UI if the Opportunity was removed because the current is currently
          // picking it up. This obviously only works for the application instance
          // that triggered the pickup, since these events don't contain any
          // info on why the op was removed.
          !op.isPickingUp,
      ),
      map((e) =>
        e.opportunity.isDispatchOpportunity
          ? SwoopingAction.OnlyDeselect
          : SwoopingAction.DeselectAndShowVanished,
      ),
    )

  // There are 2 ways we can observe this behavior:
  return merge(
    // 1: When trying to pick up an Op and the result being Vanished
    vanishedOnPickup$.pipe(map(() => SwoopingAction.DeselectAndShowVanished)),
    // 2: Observing an Opportunity Removed event for the currently selected opportunity.
    selected$.pipe(
      // switchMap here makes sure we unsubscribe from the
      // previously selected Op's status changes whenever
      // the selection changes.
      switchMap((op) =>
        op
          ? // When an Op is selected, observe when it gets removed.
            opportunityGetsRemoved(op)
          : // Otherwise do nothing.
            EMPTY,
      ),
    ),
  )
}
